import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nameCardAPI } from '../services/api';
import toast from '../utils/toast';

const QUERY_KEYS = {
  nameCard: ['name-card'],
};

/**
 * React Query hook for Digital Name Card CRUD.
 * Provides card data, loading states, and mutation helpers.
 */
export const useNameCard = (options = {}) => {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const {
    data: nameCard,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.nameCard,
    queryFn: async () => {
      try {
        const response = await nameCardAPI.getMyNameCard();
        return response.data;
      } catch (err) {
        if (err.response?.status === 404) return null;
        if (err.response?.status === 403) return null;
        throw err;
      }
    },
    staleTime: 2 * 60 * 1000,
    enabled,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404 || error?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await nameCardAPI.createNameCard(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.nameCard });
      toast.success('Digital name card created');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to create name card');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await nameCardAPI.updateNameCard(data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.nameCard, data);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to update name card');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await nameCardAPI.deleteNameCard();
    },
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEYS.nameCard, null);
      toast.success('Digital name card removed and consent revoked');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to delete name card');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await nameCardAPI.regenerateLink();
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.nameCard, data);
      toast.success('Link regenerated. Old link is permanently invalid.');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to regenerate link');
    },
  });

  return {
    nameCard: nameCard || null,
    hasCard: !!nameCard,
    isLoading,
    error,
    refetch,
    createNameCard: createMutation.mutate,
    updateNameCard: updateMutation.mutate,
    deleteNameCard: deleteMutation.mutate,
    regenerateLink: regenerateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRegenerating: regenerateMutation.isPending,
  };
};
